/**
 * Device Config API Bridge
 *
 * Client-side API for device configuration stored in ~/.wildlark/device.json.
 * Communicates with Electron main process via wildlarkAPI.
 */

// ============================================================================
// Type Definitions
// ============================================================================

export interface DeviceConfig {
    deviceId: string
    telemetryDisabled?: boolean
    wasGenerated?: boolean
    readFailed?: boolean
}

// ============================================================================
// Device Config API Functions
// ============================================================================

/**
 * Get the device configuration from ~/.wildlark/device.json.
 * Creates a new device ID if one doesn't exist.
 */
export async function getDeviceConfig(): Promise<DeviceConfig | null> {
    if (!window.wildlarkAPI) {
        console.warn("[DeviceConfigAPI] Not running in Electron, cannot get device config")
        return null
    }

    try {
        const response = (await window.wildlarkAPI.settings.getDeviceConfig()) as DeviceConfig
        return response
    } catch (error) {
        console.error("[DeviceConfigAPI] Failed to get device config:", error)
        return null
    }
}

/**
 * Restore or replace the device ID in ~/.wildlark/device.json.
 * Used when renderer-side backups prove the main config regenerated.
 */
export async function setDeviceId(deviceId: string): Promise<DeviceConfig | null> {
    if (!window.wildlarkAPI) {
        console.warn("[DeviceConfigAPI] Not running in Electron, cannot set device ID")
        return null
    }

    try {
        const response = (await window.wildlarkAPI.settings.setDeviceId(deviceId)) as DeviceConfig
        return response
    } catch (error) {
        console.error("[DeviceConfigAPI] Failed to set device ID:", error)
        return null
    }
}

/**
 * Update the telemetry disabled preference.
 * This syncs the preference to ~/.wildlark/device.json for the main process.
 */
export async function setTelemetryDisabled(disabled: boolean): Promise<void> {
    if (!window.wildlarkAPI) {
        console.warn("[DeviceConfigAPI] Not running in Electron, cannot set telemetry preference")
        return
    }

    try {
        await window.wildlarkAPI.settings.setTelemetryDisabled(disabled)
    } catch (error) {
        console.error("[DeviceConfigAPI] Failed to set telemetry preference:", error)
    }
}
